import { AuthFacade } from 'src/app/store/auth/facade/auth.facade';
import * as UserActions from './../actions/user.actions';
import * as InvitationActions from './../actions/invitations.actions';
import { tap, map, filter } from 'rxjs/operators';
import { TaxRegistrationStatus } from 'src/app/models/entity';
import { AccountVM } from './../../../models/account';
import { selectUserProfile, selectTaxStatus, selectViewProfile, getUserRequiredToSetTwoFactorAuthenticationStatus, selectUserAddress, getUserInvitations, userInvitationsLoading } from './../selectors/user.selectors';
import { Injectable } from '@angular/core';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { IAppState } from 'src/app/store/state/app.state';
import { ViewProfileState } from '../reducers/user.view.reducer';
import { IUserAddress } from '../../../models/userAddress';
import { Invitation, InvitationStatus } from '../../../models/invitation';
import * as moment from 'moment';
import { NotificationCategory, UserNotification } from 'src/app/models/userNotification';
import { AppConfig } from 'src/app/app.config';
import { Features } from 'src/app/models/features';
import { UserService } from 'src/app/services/http/user.service';
import { AuthService } from 'src/app/services/http/auth.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IdentityUser } from 'src/app/models/auth';
export interface ITaxStatus {
  taxNumber: string;
  taxRegistrationStatus: TaxRegistrationStatus;
}

export interface ITwoFactorAuthenticationStatus {
  twoFactorEnabled: boolean;
  twoFactorType: number;
  isMobileVerified: boolean;
}

export interface IUserActionsRequired {
  taxStatus: ITaxStatus;
  doPasswordChange: boolean;
  userNeedsToSetTwoFactorAuthentication: boolean;
}

@Injectable()
@UntilDestroy()
export class UserFacade {

  private getUserProfile$: Observable<AccountVM> = this.store.pipe(select(selectUserProfile));
  private isUserLoggedIn$: Observable<boolean> = this.authFacade.userLoggedIn$();
  private getUserAddress$: Observable<IUserAddress> = this.store.pipe(select(selectUserAddress));

  public userProfile$ = combineLatest([
    this.getUserProfile$,
    this.isUserLoggedIn$
  ]).pipe(
    filter((values: [AccountVM, boolean]) => {
      const isLoggedIn = values[1];
      return !!isLoggedIn;
    }),
    map((values: [AccountVM, boolean]) => {
      const [userprofile] = values;
      return userprofile;
    }),
    tap((userprofile: AccountVM) => {
      if (!userprofile) {
        this.store.dispatch(new UserActions.GetUserProfile());
      }
    })
  );

  public userEntityId$: Observable<string> = this.store.pipe(
    select(selectUserProfile),
    filter((account) => !!account),
    filter((account) => !!account.userEntityId),
    map((account) => account.userEntityId)
  );

  public userProfileWhenNotNull$: Observable<AccountVM> = this.userProfile$.pipe(
    filter(userProfile => !!userProfile)
  );

  public viewprofile$: Observable<ViewProfileState> = this.store.pipe(select(selectViewProfile));

  public verificationStatus$: Observable<number> = this.userProfileWhenNotNull$.pipe(
    map(userProfile => userProfile.verificationStatus)
  );

  public doPasswordChange$: Observable<boolean> = this.userProfileWhenNotNull$.pipe(map(userProfile => userProfile.doPasswordChange));
  public taxStatus$: Observable<ITaxStatus> = this.store.pipe(select(selectTaxStatus));
  public userNeedsToSetTwoFactorAuthentication$: Observable<boolean> = this.store.pipe(select(getUserRequiredToSetTwoFactorAuthenticationStatus));

  public UserActionsRequired$: Observable<IUserActionsRequired> = combineLatest([
    this.taxStatus$,
    this.doPasswordChange$,
    this.userNeedsToSetTwoFactorAuthentication$
  ]).pipe(
    map((values: [ITaxStatus, boolean, boolean]) => {
      const [taxStatus, doPasswordChange, userNeedsToSetTwoFactorAuthentication] = values;
      return {
        taxStatus,
        doPasswordChange,
        userNeedsToSetTwoFactorAuthentication
      };
    })
  );

  private viewProfileSubscription: Subscription;

  constructor(
    private store: Store<IAppState>,
    private authFacade: AuthFacade,
    private appConfig: AppConfig,
    private userService: UserService,
    private authService: AuthService) {
    this.setSubscriptions();
  }

  setSubscriptions() {
    this.viewProfileSubscription = combineLatest([
      this.isUserLoggedIn$,
      this.viewprofile$,
      this.userProfileWhenNotNull$])
      .pipe(untilDestroyed(this))
      .subscribe((values: [boolean, ViewProfileState, AccountVM]) => {
        const [isUserLoggedIn, viewProfile, userProfile] = values;
        if (!viewProfile || !isUserLoggedIn) {
          return;
        }
        if (viewProfile && !!userProfile.userEntityId) {
          if (!viewProfile.taxStatus || (!viewProfile.taxStatus.taxNumber && !viewProfile.taxStatus.taxRegistrationStatus)) {
            this.checkTaxStatus();
          }
          if (viewProfile.requireUserToSetTwoFactorAuthentication == null) {
            this.checkUserRequiredToSetTwoFactorAuthentication();
          }
        }
      });
  }

  checkTaxStatus() {
    this.store.dispatch(new UserActions.GetTaxStatus());
  }

  checkUserRequiredToSetTwoFactorAuthentication() {
    this.store.dispatch(new UserActions.GetUserRequiredToSetTwoFActorAuthentication());
  }

  loadUserProfile() {
    this.store.dispatch(new UserActions.GetUserProfile());
  }

  getUserProfile() : Observable<any> {
    return this.userService.getUserProfile();
  }

  getUserAddress(): Observable<IUserAddress> {
    return combineLatest([
      this.getUserAddress$,
      this.isUserLoggedIn$
    ]).pipe(
      filter((values: [IUserAddress, boolean]) => {
        const isLoggedIn = values[1];
        return !!isLoggedIn;
      }),
      map((values: [IUserAddress, boolean]) => {
        const [userAddress] = values;
        return userAddress;
      }),
      tap((userAddressValue: IUserAddress) => {
        if (!userAddressValue) {
          this.loadUserAddress();
        }
      })
    )
  }

  loadUserAddress() {
    let sub = this.userEntityId$.subscribe((userid: string) => {
      this.store.dispatch(new UserActions.GetAddress(userid));
      setTimeout(() => {
        sub.unsubscribe();
      }, 500)
    });
  }
  setUserAddress(userAddress: IUserAddress) {
    let sub = this.userEntityId$.subscribe((userid: string) => {
      const oRequestAddressChange = {
        userId: userid,
        body: userAddress
      };
      this.store.dispatch(new UserActions.SetAddress(oRequestAddressChange));
      setTimeout(() => {
        sub.unsubscribe();
      }, 500);
    });
  }

  getUserGreeting(){
    return (this.appConfig.getSection(Features.SIGN_IN) && !!this.appConfig.getSection(Features.SIGN_IN).bannerGreetingText) ? this.appConfig.getSection(Features.SIGN_IN).bannerGreetingText : "Hello";
  }

  public getUsers(): Observable<IdentityUser[]> {
    return this.authService.getUsers();
  }

  //----------------------------------------------------------------------------------------------------

  public invitationsLoading$: Observable<boolean> = this.store.pipe(select(userInvitationsLoading));

  public getUserInvitations$: Observable<Invitation[]> = this.store.pipe(select(getUserInvitations));

  /**
   * Dispatches a request to load user invitations.
   */
  public loadUserInvitations(){
    this.store.dispatch(new InvitationActions.GetUserInvitations);
  }

  public acceptUserInvitiation(invitationId : number){
    this.store.dispatch(new InvitationActions.AcceptUserInvitation(invitationId));
  }

  public rejectUserInvitiation(invitationId: number) {
    this.store.dispatch(new InvitationActions.RejectUserInvitation(invitationId));
  }

  public getCountActivePendingUserInvitations(): Observable<number> {
    return this.getUserInvitations$.pipe(map((invitations: Invitation[]) => {
      var activePendingInvitations = invitations.filter(x => x.status == InvitationStatus.Pending && !x.isExpired);
      return activePendingInvitations.length
    })
  )};
}
